import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  GridItem,
  Portal,
  Text,
} from "@chakra-ui/react";
import { LightMode } from "../ui/color-mode";
import { AppColors } from "@/styles/colors";
import { useEffect, useState } from "react";
import DatePicker from "@/components/date-picker";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { SelectComponent } from "../select/Select";
import dayjs from "dayjs";

interface Props {
  setOpen: (flag: boolean) => void;
  open: boolean;
  orderId: number;
  loadingConfirm: boolean;
  titleText?: string;
  confirmButtonLabel?: string;
  loadingDrivers?: boolean;
  loadingEquipments?: boolean;
  onDriverOrDateChange?: (params: {
    driverId: number | null;
    deliveryDate: string | null;
  }) => void;
  handleConfirmOrder: (payload: {
    orderId: number;
    driverId: number;
    equipmentId: number;
    deliveryDate: string;
  }) => Promise<void>;
  driverOptions: { label: string; value: number }[];
  equipmentOptions: { label: string; value: number }[];
}

export const ConfirmFertSlotRequestOrderModal = ({
  open,
  setOpen,
  orderId,
  loadingConfirm,
  titleText = "Asignar pedido",
  confirmButtonLabel = "Asignar",
  loadingDrivers = false,
  loadingEquipments = false,
  onDriverOrDateChange,
  handleConfirmOrder,
  driverOptions,
  equipmentOptions,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, reset, getValues, setValue } = useForm<FieldValues>({
    defaultValues: {
      driver: [] as number[],
      equipment: [] as number[],
      deliveryDate: undefined,
    },
  });

  const driver = useWatch({ name: "driver", control });
  const equipment = useWatch({ name: "equipment", control });
  const deliveryDate = useWatch({ name: "deliveryDate", control });

  const handleSetOpen = (e: any) => {
    reset({
      driver: [],
      equipment: [],
      deliveryDate: undefined,
    });
    if (!e.open) {
      onDriverOrDateChange?.({ driverId: null, deliveryDate: null });
    }
    setOpen(e.open);
  };

  const handleDriverChange = (value: any) => {
    const selected = Array.isArray(value) ? value?.[0] : value;
    const driverId = typeof selected === "number" ? selected : Number(selected);

    setValue("equipment", []);
    onDriverOrDateChange?.({
      driverId: Number.isFinite(driverId) ? driverId : null,
      deliveryDate: deliveryDate ?? null,
    });
  };

  useEffect(() => {
    const selected = driver?.[0];
    const driverId = typeof selected === "number" ? selected : Number(selected);

    if (!driver?.length || !deliveryDate) return;

    setValue("equipment", []);
    onDriverOrDateChange?.({
      driverId: Number.isFinite(driverId) ? driverId : null,
      deliveryDate: deliveryDate ?? null,
    });
  }, [deliveryDate]);

  const submitConfirm = async () => {
    const values = getValues() as {
      driver: number[];
      equipment: number[];
      deliveryDate: string;
    };

    const driverId = Number(values?.driver?.[0]);
    const equipmentId = Number(values?.equipment?.[0]);

    await handleConfirmOrder({
      orderId,
      driverId,
      equipmentId,
      deliveryDate: values.deliveryDate,
    });

    reset({
      driver: [],
      equipment: [],
      deliveryDate: undefined,
    });
    setOpen(false);
  };

  const hasDriverSelected = Boolean(driver?.length);
  const hasDateSelected = Boolean(deliveryDate);
  const canConfirm = Boolean(
    driver?.length && equipment?.length && deliveryDate,
  );
  const actionVerb = (confirmButtonLabel ?? "Asignar").toLowerCase();

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={handleSetOpen}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content
              data-testid="confirm-order-modal"
              width="90vw"
              maxWidth="780px"
              overflow="visible"
            >
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  {titleText}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body
                paddingX="24px"
                paddingY="8px"
                width="100%"
                overflow="visible"
              >
                <Flex flexDir="column" gap="12px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Seleccione chofer, equipo y fecha de entrega para{" "}
                    {actionVerb} el pedido
                  </Text>

                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                    gap="12px"
                    alignItems="start"
                  >
                    <GridItem>
                      <SelectComponent
                        control={control}
                        name="driver"
                        labelText="Chofer"
                        placeholder="Seleccione"
                        items={driverOptions}
                        fullWidth
                        loading={loadingDrivers}
                        disabled={loadingConfirm || !isActive || loadingDrivers}
                        clearable
                        handleChange={handleDriverChange}
                        minHeight="40px"
                      />
                    </GridItem>
                    <GridItem>
                      <DatePicker
                        control={control}
                        name="deliveryDate"
                        labelText="Fecha entrega"
                        placeholder="Ingresar fecha"
                        disabled={loadingConfirm || !isActive}
                        minDate={dayjs()}
                      />
                    </GridItem>
                    <GridItem>
                      <SelectComponent
                        control={control}
                        name="equipment"
                        labelText="Equipo"
                        placeholder="Seleccione"
                        items={equipmentOptions}
                        fullWidth
                        loading={loadingEquipments}
                        disabled={
                          loadingConfirm ||
                          !isActive ||
                          loadingEquipments ||
                          !hasDriverSelected ||
                          !hasDateSelected
                        }
                        clearable
                        minHeight="40px"
                      />
                    </GridItem>
                  </Grid>
                </Flex>
              </Dialog.Body>
              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={submitConfirm}
                  loading={loadingConfirm}
                  disabled={loadingConfirm || !canConfirm}
                >
                  {confirmButtonLabel}
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
